/*
 * Pricing page content (Figma "Pricing").
 * Personal compares three plans (Essential / Pro / Prestige); Business is a single plan.
 * Each row carries one `PriceValue` per plan (aligned with `plans`), except in a
 * `shared` group, where a fee that is the same on every plan is listed once.
 * `free` / `highlight` render the value in red; `down` prefixes a ↓ arrow;
 * `info` attaches a tooltip (shown via the small info icon).
 */

export interface PriceValue {
  text: string;
  /** Render in red — used for the "Free" values. */
  free?: boolean;
  /** Render in red — used to emphasise a better Pro price. */
  highlight?: boolean;
  /** Prefix a downward arrow (e.g. a reduced fee). */
  down?: boolean;
  /** Tooltip copy; presence renders the info icon. */
  info?: string;
  href?: string;
  /** Renders a tick instead of `text`, which becomes its accessible label. */
  included?: boolean;
  /** Smaller lines under the value, e.g. a fee's minimum and maximum. */
  details?: string[];
}

export interface PricingRow {
  label: string;
  /** One value per plan, in the same order as `PricingContent.plans`, or one value in a shared group. */
  values: PriceValue[];
  /**
   * Card pricing. Marked structurally rather than matched on the label, because
   * the label is translated and the Arabic table has to hide the same rows.
   * Dropped from the rendered table while `SHOW_CARD_PRICING` is false.
   */
  cards?: boolean;
}

export interface PricingGroup {
  title: string;
  rows: PricingRow[];
  /** Not live yet: the group's rows render greyed out. */
  comingSoon?: boolean;
  /**
   * The same on every plan: each row carries a single value, shown once across
   * the plan columns under an "On every plan" heading.
   */
  shared?: boolean;
  /** See `PricingRow.cards` — hides the whole group. */
  cards?: boolean;
}

export interface PricingPlan {
  name: string;
  price: string;
  /**
   * Suffix rendered straight after `price` ("$4.99" + "/m"). It is the rendered
   * string, not a description of one, so a translated table shows its own
   * cadence instead of an English "/m". Empty for a free plan.
   */
  cadence: string;
}

export interface PricingContent {
  plans: PricingPlan[];
  groups: PricingGroup[];
}

export type Audience = 'personal' | 'business';

/** Card art shown at the top of a plan card (Figma "Choose Your Plan"). */
export type PlanArt = 'standard' | 'pro' | 'prestige';

export interface PlanFeature {
  text: string;
  /** Red inline link rendered after `text`. */
  link?: { label: string; href: string };
  /** Not live yet: listed last, greyed out, under a "Coming soon" label. */
  comingSoon?: boolean;
}

export interface PlanCard {
  name: string;
  /** One line between the plan name and its price. */
  tagline: string;
  price: string;
  regularPrice?: string;
  art: PlanArt;
  promotion?: string;
  features: PlanFeature[];
  cta: string;
}

/**
 * The default pricing view: one summary card per personal plan. Copy mirrors the
 * app's plan cards (`utils/planComparison.ts` in nsave).
 */
export const plansMeta = {
  title: 'Plans – nsave',
  description:
    "Compare nsave's Essential, Pro and Prestige plans. Get USD, EUR and GBP accounts, a Visa card and card cashback. Pro is just $5.99/month.",
};

export const pricingMeta = {
  title: 'Pricing – nsave',
  description:
    'See every nsave fee in one place: what each plan includes, then the fees that are the same on every plan for receiving, sending and card payments.',
};

export const planCards: PlanCard[] = [
  {
    name: 'Essential',
    tagline: 'Receive your USD salary for free',
    price: '$0',
    art: 'standard',
    features: [
      { text: 'Free USD ACH account' },
      { text: 'Receive for free' },
      { text: 'Visa Platinum card for $1.99' },
      { text: 'USD, EUR & GBP accounts for $5.99 each, then $0.99/m each' },
      { text: '1% fee to convert USD, EUR and GBP' },
    ],
    cta: 'Get Essential',
  },
  {
    name: 'Pro',
    tagline: 'Higher rewards, lower fees',
    price: '$5.99/m',
    art: 'pro',
    features: [
      { text: 'Everything in Essential' },
      { text: 'Visa Platinum card included' },
      { text: '0.5% card cashback' },
      { text: 'Free USD & EUR wire accounts' },
      { text: 'GBP account for $5.99, then $0.99/m' },
      { text: '0.5% fee to convert USD, EUR and GBP' },
      { text: 'Investing commission capped at $15' },
      { text: 'Choose your own @ntag' },
      { text: '2% USD balance rewards, p.a', comingSoon: true },
    ],
    cta: 'Get Pro',
  },
  {
    name: 'Prestige',
    tagline: 'Our best rewards and rates',
    price: '$29.99/m',
    regularPrice: '$59.99',
    art: 'prestige',
    promotion: 'Launch offer',
    features: [
      { text: 'Everything in Pro' },
      { text: 'Visa Infinite card included' },
      { text: '1% card cashback' },
      { text: 'Free EUR, GBP and USD wire accounts' },
      { text: '0.5% fee to convert USD, EUR and GBP' },
      { text: 'Investing commission capped at $10' },
      { text: 'Limit and stop orders' },
      { text: 'Choose your own @ntag' },
      { text: '4% USD balance rewards, p.a', comingSoon: true },
    ],
    cta: 'Get Prestige',
  },
];

const free: PriceValue = { text: 'Free', free: true };
const none: PriceValue = { text: '—' };

const included: PriceValue = { text: 'Included', included: true };

const personal: PricingContent = {
  plans: [
    { name: 'Essential', price: 'Free', cadence: '' },
    { name: 'Pro', price: '$5.99', cadence: '/m' },
    { name: 'Prestige', price: '$29.99', cadence: '/m' },
  ],
  groups: [
    {
      title: 'Plan features',
      rows: [
        { label: 'Platinum card', values: [{ text: '$1.99' }, included, included], cards: true },
        { label: 'Infinite card', values: [none, none, included], cards: true },
        {
          label: 'Card cashback',
          values: [none, { text: '0.5%' }, { text: '1%', details: ['first $5k/m', 'then 0.5%'] }],
          cards: true,
        },
        {
          label: 'Convert USD, EUR and GBP',
          values: [{ text: '1%' }, { text: '0.5%' }, { text: '0.5%' }],
        },
        {
          label: 'USD and EUR wire accounts',
          values: [
            {
              text: '$5.99',
              details: ['per account', 'then $0.99/m'],
              info: 'EU residents open EUR and GCC residents open USD for free.',
            },
            included,
            included,
          ],
        },
        {
          label: 'GBP account',
          values: [
            { text: '$5.99', details: ['then $0.99/m'], info: 'Free for UK residents.' },
            { text: '$5.99', details: ['then $0.99/m'], info: 'Free for UK residents.' },
            included,
          ],
        },
        {
          label: 'Investing commission',
          values: [
            { text: '0.2%', details: ['min $0.25'] },
            { text: '0.2%', details: ['min $0.25', 'max $15'] },
            { text: '0.2%', details: ['min $0.25', 'max $10'] },
          ],
        },
        { label: 'Limit and stop orders', values: [none, none, included] },
        { label: 'Custom @ntag', values: [none, included, included] },
      ],
    },
    {
      title: 'Coming soon',
      comingSoon: true,
      rows: [{ label: 'Yield on USD', values: [none, { text: '2%' }, { text: '4%' }] }],
    },
    {
      title: 'Receive',
      shared: true,
      rows: [
        { label: 'USD ACH Details (Routing & Account number)', values: [free] },
        {
          label: 'Receive money in USD (SWIFT/ACH)',
          values: [{ text: 'Free', free: true, info: 'No fee to receive USD via SWIFT or ACH.' }],
        },
        { label: 'Deposit via Stablecoin', values: [free] },
      ],
    },
    {
      title: 'Send',
      shared: true,
      rows: [
        { label: 'nsave to nsave transfers', values: [free] },
        {
          label: 'Withdraw to your local currency',
          values: [
            {
              text: "Check today's rate",
              href: '/send-money-home',
              info: 'Fees vary by amount and currency.',
            },
          ],
        },
        { label: 'Send USD internationally (ACH)', values: [{ text: '$0.5' }] },
        { label: 'Send USD internationally (SWIFT)', values: [{ text: '$12.99' }] },
        { label: 'Send GBP (FPS)', values: [{ text: '$1' }] },
        { label: 'Send EUR (SEPA)', values: [{ text: '$1' }] },
        { label: 'Send to a stablecoin wallet', values: [{ text: '1%' }] },
        { label: 'Send USD to an Egyptian bank account', values: [{ text: '$3.50' }] },
      ],
    },
    {
      title: 'Card payments',
      shared: true,
      cards: true,
      rows: [
        { label: 'Card payments in USD', values: [free] },
        {
          label: 'Card payments in a currency other than the currency of your card account',
          values: [{ text: '1.5%' }],
        },
      ],
    },
  ],
};

const business: PricingContent = {
  plans: [{ name: 'Standard', price: '$0.00', cadence: '/m' }],
  groups: [
    {
      title: 'Accounts',
      rows: [
        { label: 'Setup fee', values: [{ text: '$200.00' }] },
        { label: 'USD ACH Details (Routing & Account number)', values: [free] },
        { label: 'USD Wire Setup (IBAN)', values: [free] },
        { label: 'GBP Account Activation', values: [free] },
        { label: 'EUR Account Activation', values: [free] },
        { label: 'FX (GBP ↔ EUR ↔ USD)', values: [{ text: '0.6%' }] },
      ],
    },
    {
      title: 'Send',
      rows: [
        { label: 'Send GBP (FPS)', values: [{ text: '$1' }] },
        {
          label: 'Send USD/EUR (SWIFT)',
          values: [{ text: '0.3% ** (min $14.99, max $24.99)' }],
        },
      ],
    },
    {
      title: 'Rewards',
      rows: [
        {
          label: 'Yearly rewards on USD balance (Coming soon)',
          values: [{ text: '3.2%', info: 'Variable rate, paid out daily.' }],
        },
      ],
    },
  ],
};

/**
 * Card pricing is shown in the public pricing table. Flip this to `false` to
 * hide the "Cards" group and the card FX row again; the content stays here
 * either way, it is only filtered out on the way to the page.
 */
export const SHOW_CARD_PRICING = true;

/**
 * Strips card pricing from a table when it is hidden. Applied once per locale in
 * `localized.ts`, so English and Arabic can never drift apart — a structural
 * mismatch between the two is a test failure, not a visual bug someone spots
 * later on `/ar`.
 */
export function withCardVisibility(content: PricingContent): PricingContent {
  if (SHOW_CARD_PRICING) return content;
  return {
    ...content,
    groups: content.groups
      .filter((group) => !group.cards)
      .map((group) => ({ ...group, rows: group.rows.filter((row) => !row.cards) }))
      .filter((group) => group.rows.length > 0),
  };
}

export const pricing: Record<Audience, PricingContent> = { personal, business };
